/**
 * Definit les paires typographiques du studio graphique.
 * Le texte arabe choisit une famille adaptee avant le poids.
 * Les familles sont partagees par le lecteur et l'editeur.
 */
import { TGDComponent } from '../../../models/courses/graphicDesign';

/** Paire de polices latines et police arabe associee. */
export interface TFontPair {
  id: string;
  label: string;
  heading: string;
  body: string;
  arabic: string;
}

/** Paires de polices disponibles dans l'interface. */
export const FONT_PAIRS: TFontPair[] = [
  { id: 'moderne', label: 'Moderne', heading: 'Bricolage Grotesque',
    body: 'Instrument Sans', arabic: 'IBM Plex Sans Arabic' },
  { id: 'ronde', label: 'Ronde', heading: 'Baloo 2', body: 'Nunito', arabic: 'Baloo Bhaijaan 2' },
  { id: 'affiche', label: 'Affiche', heading: 'Anton', body: 'Work Sans', arabic: 'Changa' },
  { id: 'elegante', label: 'Élégante', heading: 'Playfair Display',
    body: 'Source Sans 3', arabic: 'Amiri' },
  { id: 'ludique', label: 'Ludique', heading: 'Fredoka', body: 'Quicksand', arabic: 'Lalezar' },
  { id: 'sobre', label: 'Sobre', heading: 'Rubik', body: 'Rubik', arabic: 'Rubik' },
];

/** Identifiant de la paire par defaut. */
export const DEFAULT_FONT_PAIR_ID = 'moderne';

/** Trouve une paire, ou la paire par defaut. */
export const getFontPair = (id: string | undefined): TFontPair =>
  FONT_PAIRS.find(pair => pair.id === id) ?? FONT_PAIRS[0];

/** Detecte des lettres dans les blocs arabes Unicode. */
export const containsArabic = (text: string | undefined): boolean =>
  /[\u0600-\u06ff\u0750-\u077f]/.test(text ?? '');

/** Choisit la famille CSS selon la langue et le poids du texte. */
export const resolveFontFamily = (
  component: Pick<TGDComponent, 'fontPair' | 'fontWeight' | 'text'>
): string => {
  const pair = getFontPair(component.fontPair);
  const family = containsArabic(component.text) ? pair.arabic
    : component.fontWeight === 800 ? pair.heading : pair.body;
  return `"${family}", sans-serif`;
};

/** URL de la feuille contenant exactement les familles et poids pris en charge. */
export const FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?'
  + 'family=Bricolage+Grotesque:wght@400;600;800&family=Instrument+Sans:wght@400;600'
  + '&family=IBM+Plex+Sans+Arabic:wght@400;600;700&family=Baloo+2:wght@400;600;800'
  + '&family=Nunito:wght@400;600;800&family=Baloo+Bhaijaan+2:wght@400;600;800'
  + '&family=Anton&family=Work+Sans:wght@400;600;800&family=Changa:wght@400;600;800'
  + '&family=Playfair+Display:wght@400;600;800&family=Source+Sans+3:wght@400;600;800'
  + '&family=Amiri:wght@400;700&family=Fredoka:wght@400;600;700'
  + '&family=Quicksand:wght@400;600;700&family=Lalezar&family=Rubik:wght@400;600;800'
  + '&display=swap';
